import React, { useMemo, useState } from 'react';
import { updatePortalAccount } from '../api';
import { errorText } from './AccountEditor';

// The business's job form: for each field, whether it's on the form, its label, a
// default, and whether it shows on their job list. The preview shows the form the way
// their people will see it on the website.

const MODE_LABELS = {
  hidden: 'Not on the form',
  optional: 'Optional',
  required: 'Required',
  fixed: 'Shown, always the default',
};

const startingConfig = (account, fields) => Object.fromEntries(fields.map((f) => {
  const saved = (account.form_config || {})[f.key] || {};
  return [f.key, { mode: f.default_mode, show: f.default_show, ...saved }];
}));

function DefaultInput({ field, value, services, onChange }) {
  if (field.type === 'photos') return <span className="pa-muted">—</span>;
  if (field.type === 'boolean') {
    return (
      <select className="pa-input" value={value === true ? 'yes' : value === false ? 'no' : ''}
              onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'yes')}>
        <option value="">No default</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (field.type === 'choice') {
    return (
      <select className="pa-input" value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">No default</option>
        {services.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  const type = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
  return (
    <input className="pa-input" type={type} value={value ?? ''}
           onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} />
  );
}

function Preview({ fields, config, account, services }) {
  const intakeText = account.default_intake === 'pickup' ? 'We pick up' : 'They drop off';
  return (
    <div className="pa-preview">
      <div className="pa-preview-title">New job (preview)</div>
      <div className="pa-preview-field">
        <span>Pickup or drop-off</span>
        <div className="pa-preview-input">
          {account.allow_intake_choice ? `${intakeText} ▾ (they can change it)` : `${intakeText} (fixed)`}
        </div>
      </div>
      {account.default_intake === 'pickup' && (
        <div className="pa-preview-field">
          <span>Pickup</span>
          <div className="pa-preview-input">As soon as possible ▾ or a date</div>
        </div>
      )}
      {fields.map((f) => {
        const conf = config[f.key];
        if (conf.mode === 'hidden') return null;
        const label = conf.label || f.label;
        let value = '';
        if (conf.mode === 'fixed') {
          value = f.type === 'choice' ? (services.find((s) => s.value === conf.default)?.label || '') : String(conf.default ?? '');
          if (f.type === 'boolean') value = conf.default ? 'Yes' : 'No';
        }
        return (
          <div key={f.key} className="pa-preview-field">
            <span>{label}{conf.mode === 'required' ? ' *' : ''}</span>
            <div className={`pa-preview-input ${conf.mode === 'fixed' ? 'fixed' : ''}`}>
              {f.type === 'photos' ? '+ Add photos' : value || (f.type === 'choice' ? 'Choose… ▾' : '')}
            </div>
            {(conf.help || f.help) && <span className="pa-help">{conf.help || f.help}</span>}
          </div>
        );
      })}
    </div>
  );
}

function FormSetup({ account, options, onSaved }) {
  const fields = options.fields;
  const initial = useMemo(() => startingConfig(account, fields), [account, fields]);
  const [config, setConfig] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const dirty = JSON.stringify(config) !== JSON.stringify(initial);

  const set = (key, patch) => {
    setConfig((c) => ({ ...c, [key]: { ...c[key], ...patch } }));
    setMessage('');
  };

  const allowedServices = (conf) => {
    const allowed = conf.options || [];
    return allowed.length ? options.services.filter((s) => allowed.includes(s.value)) : options.services;
  };

  const toggleService = (value, on) => {
    const current = config.service.options || [];
    const next = on ? [...current, value] : current.filter((v) => v !== value);
    set('service', { options: options.services.map((s) => s.value).filter((v) => next.includes(v)) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const data = await updatePortalAccount(account.id, { form_config: config });
      onSaved(data);
      setConfig(startingConfig(data, fields)); // as the server cleaned it up
      setMessage('Saved.');
    } catch (err) {
      setMessage(errorText(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  const fixedWithoutDefault = fields.filter((f) => config[f.key].mode === 'fixed'
    && (config[f.key].default === null || config[f.key].default === undefined || config[f.key].default === ''));

  return (
    <div className="pa-form-setup">
      <div className="pa-card pa-form-table-wrap">
        <table className="pa-table pa-form-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>On the form</th>
              <th>Label they see</th>
              <th>Default</th>
              <th title="Shown on their job list and job page">On their jobs</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => {
              const conf = config[f.key];
              return (
                <React.Fragment key={f.key}>
                  <tr>
                    <td className="pa-field-name">{f.label}</td>
                    <td>
                      <select className="pa-input" value={conf.mode} onChange={(e) => set(f.key, { mode: e.target.value })}>
                        {f.modes.map((m) => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className="pa-input" placeholder={f.label} value={conf.label || ''}
                             onChange={(e) => set(f.key, { label: e.target.value })} />
                    </td>
                    <td>
                      <DefaultInput field={f} value={conf.default} services={allowedServices(conf)}
                                    onChange={(value) => set(f.key, { default: value })} />
                    </td>
                    <td className="pa-center">
                      <input type="checkbox" checked={Boolean(conf.show)} onChange={(e) => set(f.key, { show: e.target.checked })} />
                    </td>
                  </tr>
                  {f.type === 'choice' && conf.mode !== 'hidden' && (
                    <tr className="pa-subrow">
                      <td />
                      <td colSpan={4}>
                        <span className="pa-help">Services they can pick (none ticked = all):</span>
                        <div className="pa-chips">
                          {options.services.map((s) => (
                            <label key={s.value} className="pa-chip">
                              <input type="checkbox" checked={(conf.options || []).includes(s.value)}
                                     onChange={(e) => toggleService(s.value, e.target.checked)} />
                              {s.label}
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <p className="pa-help">
          A field that's not on the form still gets its default on every job (for example, always tire disposal).
          Contact comes from Settings and customers never see it.
        </p>
        {fixedWithoutDefault.length > 0 && (
          <p className="pa-warning">
            {fixedWithoutDefault.map((f) => f.label).join(', ')}: “always the default” needs a default.
          </p>
        )}
        <div className="pa-actions">
          <button type="button" className="pa-button" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save form'}
          </button>
          {dirty && <button type="button" className="pa-link" onClick={() => setConfig(initial)}>Undo changes</button>}
          {message && <span className="pa-message">{message}</span>}
        </div>
      </div>
      <Preview fields={fields} config={config} account={account} services={options.services} />
    </div>
  );
}

export default FormSetup;
